import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Col, Form, Input, Row, Select, InputNumber, Space, Button, Skeleton } from 'antd';
import { getEmpleados } from '../../../../apis/OdooApi';
import { getAll as getConceptos } from '../../../../apis/ConceptoApi';
import { obtenerAcumulados } from '../../../../apis/CorreccionAcumuladosAnualesApi';
import _ from 'lodash';

const { Option } = Select;

const FormCorreccion = forwardRef(function FormCorreccion({ onOk, onCancel, form, disabledForm }, ref) {
    const [disabled, setDisabled] = useState(disabledForm ?? false);
    const [empleados, setEmpleados] = useState([]);
    const [conceptos, setConceptos] = useState([]);
    const [acumulados, setAcumulados] = useState([]);
    const [selectedEmpleado, setSelectedEmpleado] = useState(null);
    const [selectedAnio, setSelectedAnio] = useState(null);
    const [loadingAcumulados, setLoadingAcumulados] = useState(false);
    const effectRan = useRef(false);

    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                await getCatalogs();
                await getEmpleadosOdoo();
            })();
        }
        return () => effectRan.current = true;
    }, []);

    useEffect(() => {
        setDisabled(disabledForm);
    }, [disabledForm]);

    const getCatalogs = async () => {
        const responseConcepts = await getConceptos();
        if (responseConcepts.ok) {
            let items = responseConcepts.payload;
            setConceptos(items);
        }
    }

    const getEmpleadosOdoo = async () => {
        const response = await getEmpleados();
        if (response.ok) {
            let items = response.payload;
            setEmpleados(items);
        }
    }

    const onChangeEmpleado = async (value) => {
        setSelectedEmpleado(value);
        await cargarAcumulados(value, selectedAnio);
    }

    const onChangeAnio = async (value) => {
        setSelectedAnio(value);
        await cargarAcumulados(selectedEmpleado, value);
    }

    const cargarAcumulados = async (empleado, anio) => {
        if (empleado && anio) {
            setLoadingAcumulados(true);
            // Limpiar el concepto seleccionado al cambiar los filtros
            form.setFieldsValue({ concepto_id: undefined });
            try {
                const response = await obtenerAcumulados(empleado, anio);
                if (response.ok && response.payload && response.payload.length > 0) {
                    setAcumulados(response.payload);
                } else {
                    setAcumulados([]);
                }
            } catch (error) {
                setAcumulados([]);
            }
            setLoadingAcumulados(false);
        } else {
            // Si falta algún dato, limpiar acumulados
            setAcumulados([]);
            form.setFieldsValue({ concepto_id: undefined });
        }
    }

    const onChangeConcepto = async (value) => {
        // Limpiar los campos de corrección
        form.setFieldsValue({
            monto: 0,
            exento: 0,
            descuento: 0,
            dias: 0,
            horas: 0,
            referencia: '',
        });
    }

    const onReset = () => {
        form.resetFields();
        setAcumulados([]);
        setSelectedEmpleado(null);
        setSelectedAnio(null);
    };

    const onCancelBtn = async () => {
        if (onCancel) {
            await onCancel();
        }
    }

    const onFinish = async () => {
        const values = form.getFieldsValue();
        const concepto = conceptos.find(x => x.id === values.concepto_id);
        if (!concepto) { return; }

        // Validar que al menos un campo de corrección tenga valor
        const tieneMonto = values.monto && values.monto !== 0;
        const tieneExento = values.exento && values.exento !== 0;
        const tieneDescuento = values.descuento && values.descuento !== 0;
        const tieneDias = values.dias && values.dias !== 0;
        const tieneHoras = values.horas && values.horas !== 0;

        if (!tieneMonto && !tieneExento && !tieneDescuento && !tieneDias && !tieneHoras) {
            return;
        }

        await onOk({ concepto });
    }



    return (
        <Form 
            disabled={disabled}
            layout="vertical"
            form={form}
            requiredMark
            autoComplete="off"
            onFinish={onFinish}>
            <Row gutter={16}>
                <Col md={{ span: 10 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="empleado_id"
                        label="Empleado"
                        rules={[
                            {
                                required: true,
                                message: 'Selecciona un empleado',
                            },
                        ]}
                    >
                        <Select 
                            placeholder="Por favor selecciona un empleado"
                            onChange={onChangeEmpleado}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {
                                empleados.map((item) => <Option key={item.value} value={item.value}>{item.text}</Option>)
                            }
                        </Select>
                    </Form.Item>
                </Col>
                <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="anio"
                        label="Año"
                        rules={[
                            {
                                required: true,
                                message: 'Ingresa un año válido',
                            },
                            {
                                type: 'number',
                                min: 2020,
                                message: 'El año debe ser mayor a 2020',
                            },
                        ]}
                    >
                        <InputNumber 
                            placeholder="Ej: 2026"
                            onChange={onChangeAnio}
                            style={{ width: '100%' }}
                            min={2020}
                        />
                    </Form.Item>
                </Col>
                <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="concepto_id"
                        label="Concepto a Corregir"
                        rules={[
                            {
                                required: true,
                                message: 'Selecciona un concepto',
                            },
                        ]}
                        help={acumulados.length === 0 && selectedEmpleado && selectedAnio ? 
                            "No hay acumulados para este empleado en el año seleccionado" : null}
                    >
                        <Select 
                            placeholder={acumulados.length === 0 ? 
                                "Selecciona empleado y año primero" : 
                                "Por favor selecciona un concepto"} 
                            onChange={onChangeConcepto}
                            disabled={loadingAcumulados || acumulados.length === 0}
                            loading={loadingAcumulados}
                            virtual={false}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {
                                acumulados.map((item) => 
                                    <Option key={item.concepto_id} value={item.concepto_id}>
                                        {item.concepto_nombre}
                                    </Option>
                                )
                            }
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            {loadingAcumulados && <Skeleton active />}
            {!loadingAcumulados && selectedEmpleado && selectedAnio && acumulados.length === 0 && (
                <div style={{ marginBottom: 16, padding: 8, background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 4 }}>
                    ℹ️ No se encontraron acumulados para este empleado en el año {selectedAnio}. 
                    Verifica que existan nóminas procesadas para este empleado en este año.
                </div>
            )}
            {acumulados.length > 0 && (
                <div style={{ marginBottom: 16, padding: 8, background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 4 }}>
                    ✓ Se encontraron {acumulados.length} conceptos con acumulados
                </div>
            )}
            <Row gutter={16}>
                <Col md={{ span: 5 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="monto"
                        label="Corrección Monto"
                        initialValue={0}
                    >
                        <InputNumber 
                            placeholder="Corrección de monto" 
                            style={{ width: '100%' }}
                            step={0.01}
                        />
                    </Form.Item>
                </Col>
                <Col md={{ span: 5 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="exento"
                        label="Corrección Exento"
                        initialValue={0}
                    >
                        <InputNumber 
                            placeholder="Corrección de exento" 
                            style={{ width: '100%' }}
                            step={0.01}
                        />
                    </Form.Item>
                </Col>
                <Col md={{ span: 5 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="descuento"
                        label="Corrección Descuento"
                        initialValue={0}
                    >
                        <InputNumber 
                            placeholder="Corrección de descuento" 
                            style={{ width: '100%' }}
                            step={0.01}
                        />
                    </Form.Item>
                </Col>
                <Col md={{ span: 4 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="dias"
                        label="Corrección Días"
                        initialValue={0}
                    >
                        <InputNumber 
                            placeholder="Corrección de días" 
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                <Col md={{ span: 5 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="horas"
                        label="Corrección Horas"
                        initialValue={0}
                    >
                        <InputNumber 
                            placeholder="Corrección de horas" 
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                    <Form.Item
                        name="referencia"
                        label="Referencia / Observaciones"
                    >
                        <Input placeholder="Por favor agrega una referencia" />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit">
                        Aplicar Corrección
                    </Button>
                    <Button htmlType="button" onClick={onReset}>
                        Limpiar
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
});

export default FormCorreccion;
